import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
} from 'react';
import type { CalEvent } from '../types';
import { moveModeFor } from '../domain/changes';
import {
  CELL_MIN,
  DAY_END_MIN,
  PX_PER_MIN,
  displaySubject,
  eventVisual,
  layoutColumns,
  type DaySegment,
  type EventVisual,
} from '../domain/day';
import { dayKeyOf, fd, hm } from '../domain/time';
import styles from './CalendarModule.module.css';

const MIN_EVENT_HEIGHT_PX = 18;
/** The event's time line only fits from this height up. */
const META_MIN_HEIGHT_PX = 30;
/** The whole day, 00:00–24:00. */
const RANGE = { startMin: 0, endMin: DAY_END_MIN };
/** Drags snap to this many minutes. */
const SNAP_MIN = 15;
/** Movement below this is a click, not a drag. */
const DRAG_THRESHOLD_PX = 4;

const VISUAL_CLASS: Record<EventVisual, string> = {
  declined: styles.evDeclined!,
  focus: styles.evFocus!,
  conflict: styles.evConflict!,
  draft: styles.evDraft!,
  invite: styles.evInvite!,
  tentative: styles.evTentative!,
  default: '',
};

/** Dragging within this distance of the timeline's top or bottom edge scrolls it. */
const EDGE_SCROLL_PX = 36;
/** The fastest it scrolls, right at (or past) the edge, per frame. */
const EDGE_SCROLL_MAX_PX = 14;

interface Drag {
  readonly id: string;
  readonly mode: 'move' | 'resize';
  readonly y0: number;
  /** How far the timeline was scrolled when the drag began. */
  readonly scroll0: number;
  readonly s0: number;
  readonly d0: number;
  readonly startMin: number;
  readonly endMin: number;
  readonly moved: boolean;
}

interface TimelineProps {
  /** The day shown: the timeline scrolls to `scrollToMin` whenever it changes. */
  readonly day: string;
  /** Minute of the day to show at the top when a day opens (the current time, today). */
  readonly scrollToMin: number;
  readonly segs: readonly DaySegment[];
  readonly clashing: ReadonlySet<string>;
  /** A meeting just moved or added: it pulses, and is scrolled into view. */
  readonly flashId: string | null;
  /** Minutes of the day already past (0 = none, 1440 = the whole day). */
  readonly pastMin: number;
  /** Where to draw the now-line; null when the day isn't today. */
  readonly nowMin: number | null;
  /** Whether the token allows changing the calendar; without it, meetings only open. */
  readonly writable: boolean;
  readonly onOpenEvent: (eventId: string) => void;
  /** Empty time was clicked: book a meeting starting then (minutes of this day). */
  readonly onBook: (startMin: number) => void;
  /** A meeting was dropped at a new time (minutes of this day). */
  readonly onMove: (event: CalEvent, startMin: number, endMin: number, how: 'move' | 'resize') => void;
}

/** Only single-day meetings can be dragged; one crossing midnight is changed in Outlook. */
function canDrag(e: CalEvent, writable: boolean): boolean {
  return (
    writable && moveModeFor(e) !== 'none' && dayKeyOf(e.start) === dayKeyOf(new Date(e.end.getTime() - 1))
  );
}

/**
 * The day as a timeline: drag a meeting to move it, drag its bottom edge to
 * resize it, click it to open it, click empty time to book. Everything snaps
 * to 15 minutes; the parent decides what a drop means (move, or propose).
 */
export function Timeline({
  day,
  scrollToMin,
  segs,
  clashing,
  flashId,
  pastMin,
  nowMin,
  writable,
  onOpenEvent,
  onBook,
  onMove,
}: Readonly<TimelineProps>) {
  const range = RANGE;
  const columns = useMemo(() => layoutColumns(segs), [segs]);
  const [ghostMin, setGhostMin] = useState<number | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollToRef = useRef(scrollToMin);
  scrollToRef.current = scrollToMin;

  const toY = (min: number) => (min - range.startMin) * PX_PER_MIN;

  // Only the timeline scrolls (the strip and headers above it stay put). It
  // starts at the current time when a day opens, then is left where the user
  // puts it: the clock ticking doesn't move it again.
  useLayoutEffect(() => {
    if (scrollRef.current)
      scrollRef.current.scrollTop = Math.max(0, scrollToRef.current - range.startMin) * PX_PER_MIN;
  }, [day, range.startMin]);

  // A meeting just moved or added that is out of view is brought a third of the
  // way down. Only when the highlight starts, not as the day re-renders.
  useLayoutEffect(() => {
    const box = scrollRef.current;
    const seg = flashId === null ? undefined : segs.find((s) => s.event.id === flashId);
    if (!box || !seg) return;
    const top = toY(seg.startMin);
    if (top < box.scrollTop || toY(seg.endMin) > box.scrollTop + box.clientHeight)
      box.scrollTop = Math.max(0, top - box.clientHeight / 3);
  }, [flashId]);

  const hours: number[] = [];
  for (let t = range.startMin; t < range.endMin; t += 60) hours.push(t);
  const halves: number[] = [];
  for (let t = range.startMin + 30; t < range.endMin; t += 60) halves.push(t);

  /** The bookable 30-minute slot under the pointer, or null over a meeting or past time. */
  const slotAt = (e: MouseEvent<HTMLDivElement>): number | null => {
    if (e.target !== e.currentTarget) return null;
    const rect = e.currentTarget.getBoundingClientRect();
    const raw = Math.floor(((e.clientY - rect.top) / PX_PER_MIN + range.startMin) / CELL_MIN) * CELL_MIN;
    const slot = Math.max(range.startMin, Math.min(range.endMin - CELL_MIN, raw));
    return slot + CELL_MIN <= pastMin ? null : slot;
  };

  const handleGridMove = (e: MouseEvent<HTMLDivElement>) => {
    if (drag) return;
    const slot = slotAt(e);
    if (slot !== ghostMin) setGhostMin(slot);
  };

  const handleGridClick = (e: MouseEvent<HTMLDivElement>) => {
    const slot = slotAt(e);
    if (slot !== null) onBook(slot);
  };

  /** Where the pointer was last seen during a drag, for scrolling while it rests near an edge. */
  const pointerYRef = useRef<number | null>(null);

  /**
   * The drag with the pointer at `clientY`. What counts is how far it has
   * moved through the day: the pointer's own movement plus however far the
   * timeline has scrolled since the drag began.
   */
  const dragTo = (d: Drag, clientY: number): Drag => {
    const scrolled = (scrollRef.current?.scrollTop ?? d.scroll0) - d.scroll0;
    const dy = clientY - d.y0 + scrolled;
    if (!d.moved && Math.abs(dy) < DRAG_THRESHOLD_PX) return d;
    const dm = Math.round(dy / PX_PER_MIN / SNAP_MIN) * SNAP_MIN;
    let startMin = d.s0;
    let endMin: number;
    if (d.mode === 'move') {
      startMin = Math.max(range.startMin, Math.min(range.endMin - d.d0, d.s0 + dm));
      endMin = startMin + d.d0;
    } else {
      endMin = Math.max(d.s0 + SNAP_MIN, Math.min(range.endMin, d.s0 + d.d0 + dm));
    }
    if (d.moved && startMin === d.startMin && endMin === d.endMin) return d;
    return { ...d, startMin, endMin, moved: true };
  };

  const handleDown = (ev: PointerEvent<HTMLButtonElement>, s: DaySegment) => {
    if (ev.button !== 0) return;
    setGhostMin(null);
    const resizing = (ev.target as HTMLElement).dataset.resize === '1';
    ev.currentTarget.setPointerCapture?.(ev.pointerId);
    pointerYRef.current = ev.clientY;
    setDrag({
      id: s.event.id,
      mode: resizing ? 'resize' : 'move',
      y0: ev.clientY,
      scroll0: scrollRef.current?.scrollTop ?? 0,
      s0: s.startMin,
      d0: s.endMin - s.startMin,
      startMin: s.startMin,
      endMin: s.endMin,
      moved: false,
    });
  };

  const handlePointerMove = (ev: PointerEvent<HTMLButtonElement>, s: DaySegment) => {
    if (drag?.id !== s.event.id || !canDrag(s.event, writable)) return;
    pointerYRef.current = ev.clientY;
    const next = dragTo(drag, ev.clientY);
    if (next !== drag) setDrag(next);
  };

  // While a meeting is being dragged near the top or bottom edge, the timeline
  // scrolls (faster the closer to the edge), and the meeting moves with it.
  const dragActive = drag?.moved === true;
  useEffect(() => {
    if (!dragActive) return;
    let frame = 0;
    const tick = () => {
      const box = scrollRef.current;
      const y = pointerYRef.current;
      if (box && y !== null) {
        const rect = box.getBoundingClientRect();
        const intoTop = rect.top + EDGE_SCROLL_PX - y;
        const intoBottom = y - (rect.bottom - EDGE_SCROLL_PX);
        const step = (into: number) =>
          Math.ceil((Math.min(into, EDGE_SCROLL_PX) / EDGE_SCROLL_PX) * EDGE_SCROLL_MAX_PX);
        let delta = 0;
        if (intoTop > 0) delta = -step(intoTop);
        else if (intoBottom > 0) delta = step(intoBottom);
        if (delta !== 0) {
          const before = box.scrollTop;
          box.scrollTop = before + delta;
          if (box.scrollTop !== before) setDrag((d) => (d ? dragTo(d, y) : d));
        }
      }
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
    // dragTo only reads refs and constants, so the loop doesn't restart as the drag moves.
  }, [dragActive]);

  const handleUp = (s: DaySegment) => {
    const d = drag;
    setDrag(null);
    pointerYRef.current = null;
    if (d?.id !== s.event.id) return;
    if (!d.moved) {
      onOpenEvent(s.event.id);
      return;
    }
    if (d.startMin !== d.s0 || d.endMin !== d.s0 + d.d0) onMove(s.event, d.startMin, d.endMin, d.mode);
  };

  const handleEventKey = (e: KeyboardEvent<HTMLButtonElement>, eventId: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpenEvent(eventId);
    }
  };

  return (
    <div ref={scrollRef} className={styles.tlScroll}>
      <div className={styles.tl} style={{ height: (range.endMin - range.startMin) * PX_PER_MIN }}>
        {hours.map((t) => (
          <div key={`h${t}`} className={styles.tlHour} style={{ top: toY(t) }}>
            <span>{hm(t)}</span>
          </div>
        ))}
        {halves.map((t) => (
          <div key={`m${t}`} className={styles.tlHalf} style={{ top: toY(t) }} />
        ))}
        {/*
          Clicking empty time books it: a mouse shortcut. Keyboard users book from the
          availability strip or "+ New", so the grid itself is presentational.
        */}
        <div
          className={styles.tlGrid}
          role="none"
          onPointerMove={handleGridMove}
          onPointerLeave={() => setGhostMin(null)}
          onClick={handleGridClick}
        >
          {pastMin > range.startMin && (
            <div className={styles.tlPast} style={{ height: toY(Math.min(pastMin, range.endMin)) }} />
          )}
          {segs.map((s) => {
            const e = s.event;
            const dragging = drag?.id === e.id && drag.moved;
            const startMin = dragging ? drag.startMin : s.startMin;
            const endMin = dragging ? drag.endMin : s.endMin;
            const layout = columns.get(e.id) ?? { col: 0, cols: 1 };
            const width = 100 / layout.cols;
            const top = toY(Math.max(startMin, range.startMin));
            const height = Math.max(
              MIN_EVENT_HEIGHT_PX,
              (Math.min(endMin, range.endMin) - Math.max(startMin, range.startMin)) * PX_PER_MIN - 2
            );
            const place = e.location ? ` · ${e.location}` : '';
            const meta =
              dragging && drag.mode === 'resize'
                ? `${hm(startMin)}–${hm(endMin)} · ${fd(endMin - startMin)}`
                : `${hm(startMin)}–${hm(endMin)}${place}`;
            const draggable = canDrag(e, writable);
            return (
              <button
                key={e.id}
                type="button"
                className={`${styles.ev} ${VISUAL_CLASS[eventVisual(e, clashing)]} ${draggable ? styles.evMovable : ''} ${
                  dragging ? styles.evDragging : ''
                } ${flashId === e.id ? styles.flash : ''}`}
                style={{
                  top,
                  height,
                  left: `calc(${layout.col * width}% + 1px)`,
                  width: `calc(${width}% - 3px)`,
                }}
                aria-label={`${displaySubject(e)}, ${meta}`}
                onPointerDown={(ev) => handleDown(ev, s)}
                onPointerMove={(ev) => handlePointerMove(ev, s)}
                onPointerUp={() => handleUp(s)}
                onPointerCancel={() => setDrag(null)}
                onKeyDown={(ev) => handleEventKey(ev, e.id)}
              >
                <span className={styles.evTitle}>{displaySubject(e)}</span>
                {height >= META_MIN_HEIGHT_PX && <span className={styles.evMeta}>{meta}</span>}
                {draggable && <span className={styles.evResize} data-resize="1" />}
              </button>
            );
          })}
          {ghostMin !== null && (
            <div className={styles.tlGhost} style={{ top: toY(ghostMin), height: CELL_MIN * PX_PER_MIN - 2 }}>
              + New · {hm(ghostMin)}
            </div>
          )}
          {nowMin !== null && nowMin >= range.startMin && nowMin <= range.endMin && (
            <div className={styles.tlNow} style={{ top: toY(nowMin) }} title={`Now · ${hm(nowMin)}`} />
          )}
        </div>
      </div>
    </div>
  );
}
